import { fetchWithAppAuth } from '@/lib/auth/client-api';
import type { User } from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc, type Firestore } from 'firebase/firestore';

import { getPrimaryRole, mergeRoles, type GlobalRole } from '@/lib/auth/roles';
import type { ProfileCompletionInput, UserDoc } from '@/types/platform';
import { uniqueStrings } from '@/lib/utils';
import { COLLECTIONS, userPath } from '@/lib/firebase/paths';

export const usersCollection = COLLECTIONS.users;
export const authorProfilesCollection = COLLECTIONS.authorProfiles;
const PRIVILEGED_ROLES: readonly GlobalRole[] = ['admin', 'owner'];

function stripPrivilegedRoles(roles: unknown) {
  if (!Array.isArray(roles)) return [];
  return roles.filter((role): role is GlobalRole => !PRIVILEGED_ROLES.includes(role as GlobalRole));
}

function getClientWritableUserDoc(doc: UserDoc) {
  const clientWritable: Partial<UserDoc> = { ...doc };
  delete clientWritable.plan;
  delete clientWritable.accountStatus;
  delete clientWritable.emailVerified;
  delete clientWritable.createdAt;
  return clientWritable;
}

function firestoreStepError(action: string, path: string, error: unknown) {
  const code =
    typeof error === 'object' && error && 'code' in error && typeof error.code === 'string'
      ? `${error.code}: `
      : '';
  const message = error instanceof Error ? error.message : 'Firestore operation failed.';
  return new Error(`${action} ${path} failed: ${code}${message}`);
}

function createBaseUserDoc(user: User): UserDoc {
  const roles = mergeRoles(['reader']);

  return {
    uid: user.uid,
    email: user.email ?? '',
    displayName: user.displayName ?? user.email?.split('@')[0] ?? 'Untangled User',
    photoURL: user.photoURL ?? null,
    roles,
    primaryRole: getPrimaryRole(roles),
    emailVerified: user.emailVerified,
    authorProfileId: null,
    plan: 'free',
    accountStatus: 'active',
    authorEnabled: false,
    authorStatus: 'none',
    onboardingCompleted: false,
  };
}

export async function ensureUserDoc(db: Firestore, user: User) {
  const path = userPath(user.uid);
  const userRef = doc(db, path);
  let snapshot;
  try {
    snapshot = await getDoc(userRef);
  } catch (error) {
    throw firestoreStepError('read', path, error);
  }
  const base = createBaseUserDoc(user);

  if (!snapshot.exists()) {
    try {
      await setDoc(userRef, {
        ...getClientWritableUserDoc(base),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
      });
    } catch (error) {
      throw firestoreStepError('create', path, error);
    }
    return base;
  }

  const data = snapshot.data() as Partial<UserDoc>;
  const mergedRoles = mergeRoles(stripPrivilegedRoles(data.roles), ['reader']);
  const merged: UserDoc = {
    ...base,
    ...data,
    displayName: data.displayName || base.displayName,
    email: user.email ?? data.email ?? '',
    photoURL: user.photoURL ?? data.photoURL ?? null,
    roles: mergedRoles,
    primaryRole: getPrimaryRole(mergedRoles),
    emailVerified: user.emailVerified,
    onboardingCompleted: Boolean(data.onboardingCompleted),
    authorProfileId: data.authorProfileId ?? null,
    plan: data.plan ?? 'free',
    accountStatus: data.accountStatus ?? 'active',
    authorEnabled: Boolean(data.authorEnabled ?? mergedRoles.includes('author')),
    authorStatus: data.authorStatus ?? (mergedRoles.includes('author') ? 'active' : data.authorProfileId ? 'disabled' : 'none'),
  };

  return merged;
}

export async function completeUserProfile(db: Firestore, user: User, input: ProfileCompletionInput, currentDoc?: UserDoc | null) {
  void db; void currentDoc;
  const response = await fetchWithAppAuth('/api/account/profile', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'complete-profile', input, expectedSessionUid: user.uid }) });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || 'Unable to complete profile.');
  return result.profile as UserDoc;
}

export function getUserInitials(displayName?: string | null, email?: string | null) {
  const source = displayName?.trim() || email?.trim() || 'User';
  const parts = source.split(/\s+/).slice(0, 2);
  return parts.map((part) => part.charAt(0).toUpperCase()).join('');
}

export function roleSummary(roles: GlobalRole[] | undefined) {
  return uniqueStrings((roles ?? []).map((role) => role.charAt(0).toUpperCase() + role.slice(1))).join(' • ');
}
